/* ============================================================================
   RYŌHŌ — galerie produit mobile. Chantier du 2026-08-05.

   Quatre responsabilités, TOUTES conditionnées à `(max-width: 749px)` et réversibles :
   au-dessus du seuil, la galerie de Dawn doit se comporter exactement comme avant, y
   compris après une rotation d'écran.

   1. Les vidéos se lisent DANS le cadre : `playsinline` posé sur chaque <video>. Sans
      lui, iOS bascule d'office en plein écran natif dès le démarrage de la lecture —
      c'est ce passage AUTOMATIQUE qu'on supprime.
      Le bouton ⛶ des contrôles natifs, lui, est laissé fonctionnel : voir la note
      « PLEIN ÉCRAN VIDÉO » plus bas, c'est un choix documenté et non un oubli.
   2. Le plein écran IMAGE est neutralisé côté DOM en plus du CSS : `pointer-events`
      n'empêche pas une activation au clavier.
   3. La tuile « +N » déplie les vignettes restantes sur place.
   4. Le média visé par une vignette est préchargé au premier contact du doigt : les
      diapos non actives ne sont plus dans le flux, donc plus jamais préchargées par le
      navigateur, et le tap devenait un écran vide de plusieurs centaines de millisecondes.

   PLEIN ÉCRAN VIDÉO — pourquoi le bouton natif reste actif.
   `controlsList="nofullscreen"` n'est pas reconnu par Safari : sur iOS le bouton ⛶ reste
   dans la barre de contrôles quoi qu'on fasse. La seule façon de l'intercepter est
   d'écouter `webkitbeginfullscreen`, qui est émis APRÈS l'entrée en plein écran : on en
   ressort donc dans la foulée, ce qui produit un aller-retour visible et met la lecture
   en pause. Un bouton qui clignote et casse la lecture est pire qu'un bouton qui marche.
   L'objectif de la consigne — « pas de plein écran AU TAP », c'est-à-dire pas de bascule
   subie — est atteint par `playsinline` seul. Le plein écran reste accessible sur
   demande explicite, ce qui redonne au passage un moyen de voir une vidéo verticale en
   entier malgré le recadrage du cadre carré.
   ============================================================================ */
(() => {
  const MOBILE = '(max-width: 749px)';
  const mql = window.matchMedia(MOBILE);
  const observateurs = new WeakMap();

  /* ---------------------------------------------------------------- 1. VIDÉOS */

  /** Pose `playsinline` sur une <video>. Idempotent, et réversible : au retour desktop,
   *  on retire ce qu'on a posé et rien d'autre. */
  const contenirVideo = (v) => {
    if (!v) return;
    if (mql.matches) {
      if (v.dataset.ryContenue === '1') return;
      v.dataset.ryContenue = '1';
      v.setAttribute('playsinline', '');
      v.setAttribute('webkit-playsinline', '');
    } else if (v.dataset.ryContenue === '1') {
      delete v.dataset.ryContenue;
      v.removeAttribute('playsinline');
      v.removeAttribute('webkit-playsinline');
    }
  };

  /** Le <template> de <deferred-media> est patché AVANT que Dawn ne le clone.
   *  Dawn appelle `loadContent()` puis `play()` dans la même tâche ; le rappel d'un
   *  MutationObserver est une microtâche, donc il arriverait APRÈS le démarrage de la
   *  lecture — exactement l'instant où iOS décide de basculer en plein écran. Patcher le
   *  gabarit supprime la course au lieu de la gagner de justesse. */
  const preparerTemplate = (dm) => {
    const t = dm.querySelector('template');
    if (!t || !mql.matches || t.dataset.ryPrepare === '1') return;
    t.dataset.ryPrepare = '1';
    t.content.querySelectorAll('video').forEach((v) => {
      v.setAttribute('playsinline', '');
      v.setAttribute('webkit-playsinline', '');
    });
  };

  const traiterGalerie = (racine) => {
    racine.querySelectorAll('deferred-media').forEach(preparerTemplate);
    racine.querySelectorAll('video').forEach(contenirVideo);
  };

  /* ------------------------------------------------- 2. PLEIN ÉCRAN IMAGE OFF */

  /** La CSS met `pointer-events: none` sur le déclencheur, ce qui suffit au doigt. Au
   *  clavier il resterait focalisable et actionnable : on le sort du parcours de
   *  tabulation et de l'arbre d'accessibilité. On ne le SUPPRIME pas — il doit revenir
   *  intact au-dessus de 750 px, où la visionneuse est toujours en service. */
  const neutraliserZoom = (racine) => {
    racine.querySelectorAll('.product__media-toggle').forEach((b) => {
      if (mql.matches) {
        if (b.dataset.ryZoom === '1') return;
        b.dataset.ryZoom = '1';
        b.dataset.ryTabindex = b.getAttribute('tabindex') || '';
        b.setAttribute('tabindex', '-1');
        b.setAttribute('aria-hidden', 'true');
      } else if (b.dataset.ryZoom === '1') {
        if (b.dataset.ryTabindex) b.setAttribute('tabindex', b.dataset.ryTabindex);
        else b.removeAttribute('tabindex');
        b.removeAttribute('aria-hidden');
        delete b.dataset.ryZoom;
        delete b.dataset.ryTabindex;
      }
    });
  };

  // Capture : on arrête l'activation avant le gestionnaire de <modal-opener>, quel que
  // soit l'ordre d'attachement. Écouteur unique, posé une seule fois sur le document.
  document.addEventListener(
    'click',
    (e) => {
      if (!mql.matches) return;
      const b = e.target.closest?.('.product__media-toggle');
      if (b && b.closest('media-gallery')) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true
  );

  /* ------------------------------------------------------------ 3. TUILE « +N » */

  /** Renvoie les vignettes réellement affichées — `hide_variants` peut en masquer, et
   *  un index positionnel tomberait alors sur un élément invisible. */
  const vignettesVisibles = (liste) =>
    [...liste.querySelectorAll('.thumbnail-list__item:not(.ry-more)')].filter(
      (t) => getComputedStyle(t).display !== 'none'
    );

  const brancherPlus = (liste) => {
    const tuile = liste.querySelector('.ry-more__button');
    if (!tuile || tuile.dataset.ryBranche === '1') return;
    tuile.dataset.ryBranche = '1';
    // C'est le script qui pose l'état replié : sans JavaScript, aucune vignette n'est
    // masquée et la tuile elle-même reste cachée (règle CSS sur :not([data-ry-more])).
    liste.setAttribute('data-ry-more', 'replie');
    const compteur = tuile.querySelector('.ry-more__count');
    const plus = tuile.dataset.ryLabelPlus || '+';
    const nomDeplier = tuile.dataset.ryLabelDeplier || 'Afficher les autres médias';
    const nomReplier = tuile.dataset.ryLabelReplier || 'Masquer les médias supplémentaires';
    const nom = tuile.querySelector('.ry-more__nom');

    const majuscule = (replie) => {
      tuile.setAttribute('aria-expanded', replie ? 'false' : 'true');
      if (compteur) compteur.textContent = replie ? plus : '−';
      // Le compteur est aria-hidden : sans cette ligne, le lecteur d'écran annoncerait
      // le même nom dans les deux états.
      if (nom) nom.textContent = replie ? nomDeplier : nomReplier;
    };
    majuscule(true);

    tuile.addEventListener('click', () => {
      const replie = liste.getAttribute('data-ry-more') === 'replie';
      liste.setAttribute('data-ry-more', replie ? 'deplie' : 'replie');
      majuscule(!replie);
      if (replie) {
        // On donne le focus à la première vignette RÉVÉLÉE, en comptant les vignettes
        // visibles : un index positionnel brut tomberait sur un élément `display: none`
        // dès que `hide_variants` est actif, et le focus retomberait sur <body>.
        const revelees = vignettesVisibles(liste).slice(6);
        revelees[0]?.querySelector('.thumbnail')?.focus?.({ preventScroll: true });
      }
    });
  };

  /* --------------------------------------------------- 4. PRÉCHARGEMENT AU TAP */

  /** Les diapos non actives sont hors flux : leurs images `loading="lazy"` ne sont donc
   *  plus jamais préchargées, et le tap sur une vignette ouvrait un cadre vide le temps
   *  du téléchargement. On lance la requête dès le contact du doigt — soit ~100 ms
   *  d'avance sur le `click`, ce qui suffit le plus souvent à masquer l'attente. */
  const brancherPrechargement = (galerie) => {
    if (galerie.dataset.ryPrefetch === '1') return;
    galerie.dataset.ryPrefetch = '1';
    galerie.addEventListener(
      'pointerdown',
      (e) => {
        if (!mql.matches) return;
        const li = e.target.closest?.('.thumbnail-list__item[data-target]');
        if (!li) return;
        const media = galerie.querySelector(`[data-media-id="${li.dataset.target}"]`);
        media?.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.setAttribute('loading', 'eager');
          img.setAttribute('fetchpriority', 'high');
        });
      },
      { passive: true }
    );
  };

  /* ------------------------------------------------------------- ORCHESTRATION */

  const traiterUne = (g) => {
    traiterGalerie(g);
    neutraliserZoom(g);
    brancherPrechargement(g);
    const liste = g.querySelector('.thumbnail-list');
    if (liste) brancherPlus(liste);
    if (!observateurs.has(g)) {
      // Dawn injecte la vidéo à la demande et réinjecte des diapos entières au changement
      // de variante : on retraite le sous-arbre à chaque mutation, zoom compris.
      const mo = new MutationObserver(() => {
        traiterGalerie(g);
        neutraliserZoom(g);
      });
      mo.observe(g, { childList: true, subtree: true });
      observateurs.set(g, mo);
    }
  };

  const appliquer = () => document.querySelectorAll('media-gallery').forEach(traiterUne);

  appliquer();

  // Une section rechargée (éditeur de thème, ajout rapide) remplace tout son DOM : sans
  // ce rebranchement, la tuile « +N » de la nouvelle galerie serait inerte et la cliente
  // conclurait, en aperçu, que la fonctionnalité est cassée.
  document.addEventListener('shopify:section:load', appliquer);
  document.addEventListener('shopify:section:select', appliquer);

  // Le seuil mobile peut être franchi sans rechargement (rotation, redimensionnement) :
  // tout ce que le script pose doit alors être posé ou retiré en conséquence.
  const surChangement = () => appliquer();
  if (mql.addEventListener) mql.addEventListener('change', surChangement);
  else mql.addListener(surChangement);
})();
